import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import { toastSuccess, toastError } from "../../utils/alertHelper";
import CommonModal from '../../components/commonComponent/CommonModal';

export default function OrderCreate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isCreateMode = !id;
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
  }, []);

  const isClientCreate = isCreateMode && user && user.userType === "Client";

  useEffect(() => {
    if (isClientCreate) setStatus("New");
  }, [isClientCreate]);

  /* ================= BASIC DETAILS ================= */
  const [customerName, setCustomerName] = useState("");
  const [customerDisplayName, setCustomerDisplayName] = useState("");
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0,10));
  const [ewayBill, setEwayBill] = useState("");
  const [lrNumber, setLrNumber] = useState("");
  const [transportNumber, setTransportNumber] = useState("");

  /* ================= ADDRESS ================= */
  const [billingAddress, setBillingAddress] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [billingState, setBillingState] = useState("");
  const [shippingState, setShippingState] = useState("");
  const [clients, setClients] = useState([]);
  const [shipToList, setShipToList] = useState([]);
  const [selectedShipToId, setSelectedShipToId] = useState("");
  const [itemOptions, setItemOptions] = useState([]);

  const [showShippingModal, setShowShippingModal] = useState(false);
  const [shippingForm, setShippingForm] = useState({
    houseNo: "",
    societyName: "",
    area: "",
    landmark: "",
    city: "",
    state: "",
    country: "",
    pinCode: "",
  });
  const [countries, setCountries] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);

  /* ================= ITEMS ================= */
  const [items, setItems] = useState([
    {
      item: "",
      description: "",
      unit: "",
      quantity: 1,
      rate: 0,
      taxRate: "",
      taxName: "",
      taxType: "",
      total: 0,
    },
  ]);

  /* ================= TOTALS ================= */
  const [discount, setDiscount] = useState(0);
  const [adjustments, setAdjustments] = useState(0);

  /* ================= OTHER ================= */
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [terms, setTerms] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("New");

  /* ================= CALCULATIONS ================= */
  const isClientUser = user?.userType === 'Client';
  const effectiveDiscount = isClientUser ? 0 : discount;
  const effectiveAdjustments = isClientUser ? 0 : adjustments;

  // Compute derived totals and per-line tax/amounts without mutating `items`
  const computeDerived = () => {
    const baseLines = items.map((it) => {
      const qty = Number(it.quantity || 0);
      const rate = Number(it.rate || 0);
      return qty * rate;
    });
    const subTotalVal = baseLines.reduce((s, v) => s + (Number(v) || 0), 0);
    const discountVal = Number(effectiveDiscount) || 0;
    const discountedSubtotal = Math.max(subTotalVal - discountVal, 0);

    const perLine = items.map((it, idx) => {
      const base = Number(baseLines[idx]) || 0;
      const share = subTotalVal > 0 ? base / subTotalVal : 0;
      const discountShare = discountVal * share;
      const netBase = Math.max(base - discountShare, 0);

      // compute tax based on taxComponents or taxRate
      let taxAmount = 0;
      let computedComponents = [];
      const comps = Array.isArray(it.taxComponents) && it.taxComponents.length ? it.taxComponents : null;
      if (comps) {
        computedComponents = comps.map((c) => {
          const pct = Number(c.percentage || c.p || 0) || 0;
          const amt = (netBase * pct) / 100;
          return { name: c.name || c.type || 'GST', type: c.type || c.name || '', percentage: pct, amount: amt };
        });
        taxAmount = computedComponents.reduce((s, c) => s + (Number(c.amount) || 0), 0);
      } else {
        const pct = Number(it.taxRate || it.gstRate || it.gst || 0) || 0;
        if (pct) {
          const amt = (netBase * pct) / 100;
          computedComponents = [{ name: it.taxName || it.taxType || 'GST', type: it.taxType || '', percentage: pct, amount: amt }];
          taxAmount = amt;
        }
      }

      return { base, netBase, taxAmount, components: computedComponents };
    });

    const totalTaxVal = perLine.reduce((s, p) => s + (Number(p.taxAmount) || 0), 0);
    const grandTotalVal = discountedSubtotal + totalTaxVal + Number(effectiveAdjustments || 0);

    return { subTotalVal, discountedSubtotal, perLine, totalTaxVal, grandTotalVal };
  };

  const derived = computeDerived();
  const subTotal = derived.subTotalVal;
  const totalTax = derived.totalTaxVal;
  const grandTotal = derived.grandTotalVal;

  // If editing an existing order, disallow edits when the order is already partially/fully processed
  const nonEditableStatuses = ["Confirmed", "Partially Dispatched", "Fully Dispatched"];
  const isOrderEditable = isCreateMode || !nonEditableStatuses.includes(status);

  /* ================= ITEM HANDLERS ================= */
  // Helper: compute tax components for an item given billing/shipping states
  const computeTaxFor = (itemObj = {}, qty = 0, rate = 0, billState = billingState, shipState = shippingState) => {
    const inter = itemObj.interStateGst || itemObj.interState || itemObj.interGst || null;
    const intra = itemObj.intraStateGst || itemObj.intraState || itemObj.intraGst || null;
    const comps = [];
    const useInter = billState && shipState ? String(billState) !== String(shipState) : false;

    if (useInter && inter && (typeof inter.percentage !== 'undefined' || Array.isArray(inter))) {
      const p = Number(inter.percentage) || 0;
      comps.push({ name: inter.name || 'IGST', type: inter.gstType || 'IGST', percentage: p, amount: (qty * rate * p) / 100 });
    } else if (!useInter && intra && Array.isArray(intra.taxes) && intra.taxes.length) {
      for (const t of intra.taxes) {
        const p = Number(t.percentage) || 0;
        comps.push({ name: t.name || t.gstType || 'GST', type: t.gstType || t.name || '', percentage: p, amount: (qty * rate * p) / 100 });
      }
    } else if (!useInter) {
      const p = Number(itemObj?.taxRate ?? itemObj?.gstRate ?? itemObj?.gst ?? itemObj?.tax ?? itemObj?.percentage ?? 0) || 0;
      if (p) {
        const half = p / 2;
        comps.push({ name: 'SGST', type: 'SGST', percentage: half, amount: (qty * rate * half) / 100 });
        comps.push({ name: 'CGST', type: 'CGST', percentage: half, amount: (qty * rate * half) / 100 });
      }
    } else {
      const p = Number(itemObj?.taxRate ?? itemObj?.gstRate ?? itemObj?.gst ?? itemObj?.tax ?? itemObj?.percentage ?? 0) || 0;
      if (p) comps.push({ name: itemObj?.gstName || itemObj?.taxName || 'GST', type: itemObj?.gstType || '', percentage: p, amount: (qty * rate * p) / 100 });
    }

    return comps;
  };
  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    if (field === "quantity" || field === "rate" || field === "offerPrice") {
      updated[index][field] = Number(value) || 0;
    } else {
      updated[index][field] = value;
    }

    if (field === "item") {
      const sel = itemOptions.find((it) => (it._id || it.id) === value);
      if (sel) {
        updated[index].description = sel.description || sel.desc || updated[index].description;
        updated[index].unit = sel.unit?.name || sel.unit || updated[index].unit;
        
        let chosenRate = updated[index].rate;
        if (typeof sel.offerPrice !== "undefined" && sel.offerPrice !== null) {
          chosenRate = sel.offerPrice;
        } else {
          
          const srList = Array.isArray(sel.stateRate) ? sel.stateRate : [];
          const stateKey = shippingState || billingState || "";
          const sr = srList.find((s) => {
            const stId = s && s.state ? (s.state._id || s.state) : null;
            return stId && String(stId) === String(stateKey);
          });
          if (sr && typeof sr.price !== 'undefined') {
            chosenRate = sr.price;
          } else if (typeof sel.salesPrice !== "undefined" && sel.salesPrice !== null) {
            chosenRate = sel.salesPrice;
          }
        }
        updated[index].rate = chosenRate;
          
          const inter = sel.interStateGst || sel.interState || sel.interGst || null;
          const intra = sel.intraStateGst || sel.intraState || sel.intraGst || null;
          
          const billKey = billingState ? String(billingState) : "";
          const shipKey = shippingState ? String(shippingState) : "";
          const useInter = billKey && shipKey ? billKey !== shipKey : false;

          if (useInter && inter && (typeof inter.percentage !== 'undefined' || typeof inter.percentage === 'number' || Array.isArray(inter))) {
            const p = (typeof inter.percentage !== 'undefined' ? Number(inter.percentage) : (Array.isArray(inter) ? 0 : 0)) || 0;
            updated[index].taxRate = p;
            updated[index].taxName = inter.name || "IGST";
            updated[index].taxType = inter.gstType || "IGST";
            const qty0 = Number(updated[index].quantity || 0);
            const rate0 = Number(updated[index].rate || 0);
            updated[index].taxComponents = [{ name: updated[index].taxName, type: updated[index].taxType, percentage: Number(updated[index].taxRate) || 0, amount: (qty0 * rate0 * (Number(updated[index].taxRate) || 0)) / 100 }];
            updated[index].taxAmount = updated[index].taxComponents.reduce((s,c)=>s+(Number(c.amount)||0),0);
          } else if (!useInter && intra && Array.isArray(intra.taxes) && intra.taxes.length) {
            const names = intra.taxes.map((t) => t.name || t.gstType || "GST").join(" ");
            const sum = intra.taxes.reduce((s, t) => s + (Number(t.percentage) || 0), 0);
            updated[index].taxRate = sum;
            updated[index].taxName = names;
            updated[index].taxType = intra.taxes.map((t) => t.gstType || t.name || "").join(" ");
            const qty1 = Number(updated[index].quantity || 0);
            const rate1 = Number(updated[index].rate || 0);
            updated[index].taxComponents = intra.taxes.map((t)=>({ name: t.name||t.gstType||'GST', type: t.gstType||t.name||'', percentage: Number(t.percentage)||0, amount: (qty1*rate1*(Number(t.percentage)||0))/100 }));
            updated[index].taxAmount = updated[index].taxComponents.reduce((s,c)=>s+(Number(c.amount)||0),0);
          } else if (!useInter) {
            
            const singlePct = (inter && (typeof inter.percentage !== 'undefined') ? Number(inter.percentage) : (typeof sel.taxRate !== 'undefined' ? Number(sel.taxRate) : (typeof sel.gstRate !== 'undefined' ? Number(sel.gstRate) : null)));
            if (singlePct || singlePct === 0) {
              const half = singlePct / 2;
              updated[index].taxRate = singlePct;
              updated[index].taxName = 'SGST CGST';
              updated[index].taxType = 'SGST CGST';
              const qty2 = Number(updated[index].quantity || 0);
              const rate2 = Number(updated[index].rate || 0);
              updated[index].taxComponents = [
                { name: 'SGST', type: 'SGST', percentage: half, amount: (qty2 * rate2 * half) / 100 },
                { name: 'CGST', type: 'CGST', percentage: half, amount: (qty2 * rate2 * half) / 100 },
              ];
              updated[index].taxAmount = updated[index].taxComponents.reduce((s,c)=>s+(Number(c.amount)||0),0);
            }
          } else if (inter && (typeof inter.percentage !== 'undefined' || typeof inter.percentage === 'number' || Array.isArray(inter))) {
            // Fallback: use inter if available even when state data missing
            const p = (typeof inter.percentage !== 'undefined' ? Number(inter.percentage) : (Array.isArray(inter) ? 0 : 0)) || 0;
            updated[index].taxRate = p;
            updated[index].taxName = inter.name || "IGST";
            updated[index].taxType = inter.gstType || "IGST";
            const qty0 = Number(updated[index].quantity || 0);
            const rate0 = Number(updated[index].rate || 0);
            updated[index].taxComponents = [{ name: updated[index].taxName, type: updated[index].taxType, percentage: Number(updated[index].taxRate) || 0, amount: (qty0 * rate0 * (Number(updated[index].taxRate) || 0)) / 100 }];
            updated[index].taxAmount = updated[index].taxComponents.reduce((s,c)=>s+(Number(c.amount)||0),0);
          } else {
            // Fallback to older single-field shapes
            const gstVal = sel.taxRate ?? sel.gstRate ?? sel.gst ?? sel.tax ?? sel.percentage ?? sel.rateOfTax ?? "";
            updated[index].taxRate = typeof gstVal !== 'undefined' && gstVal !== null ? gstVal : updated[index].taxRate;
            updated[index].taxName = sel.gstName || sel.taxName || "";
            updated[index].taxType = sel.gstType || updated[index].taxType || "";
          }
      }
    }

    
    const qty = Number(updated[index].quantity || 0);
    const rate = Number(updated[index].rate || 0);
    updated[index].total = qty * rate;

    // compute tax components and taxAmount
    const components = Array.isArray(updated[index].taxComponents) ? [...updated[index].taxComponents] : [];
    if (components.length === 0) {
      
      if (updated[index].taxRate !== "" && updated[index].taxRate != null) {
        components.push({
          name: updated[index].taxName || (updated[index].taxType || "GST"),
          type: updated[index].taxType || "GST",
          percentage: Number(updated[index].taxRate) || 0,
          amount: (qty * rate * (Number(updated[index].taxRate) || 0)) / 100,
        });
      }
    } else {
      // recalc amounts from percentages
      for (let c of components) {
        c.amount = (qty * rate * (Number(c.percentage) || 0)) / 100;
      }
    }

    // store components and taxAmount
    updated[index].taxComponents = components;
    updated[index].taxAmount = components.reduce((s, c) => s + (Number(c.amount) || 0), 0);

    setItems(updated);
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await axios.get("/api/user?userType=Client");
        const users = res?.data?.data?.users || [];
        setClients(users);
      } catch (err) {
        console.error("Failed to fetch clients", err);
      }
    };

    const fetchItems = async () => {
      try {
        const res = await axios.get("/admin/api/item");
        const payload = res?.data;
        let list = [];

        if (Array.isArray(payload?.data?.items)) list = payload.data.items;
        else if (Array.isArray(payload?.data)) list = payload.data;
        else if (Array.isArray(payload?.Items)) list = payload.Items;
        else if (Array.isArray(payload?.items)) list = payload.items;
        else if (Array.isArray(payload)) list = payload;
        else list = [];

        const available = Array.isArray(list)
          ? list.filter((it) => it && (it.isAvailable === true || it.isAvailable === 'true'))
          : [];
        setItemOptions(available);
      } catch (err) {
        console.error("Failed to fetch items", err);
      }
    };

    fetchClients();
    fetchItems();
    fetchCountries();
  }, []);

  useEffect(() => {
    if (id) return; // editing existing order -> don't override

    try {
      const storedUser = localStorage.getItem('user');
      const curUser = storedUser ? JSON.parse(storedUser) : null;

      // Always prefill basic customer info from current user when available
      if (curUser) {
        setCustomerName(curUser._id || curUser.id || '');
        setCustomerDisplayName(((curUser.firstName || '') + (curUser.lastName ? ` ${curUser.lastName}` : '')) || curUser.displayName || curUser.name || '');
        if (curUser.bill || curUser.billTo) {
          setBillingAddress(formatBillAddress(curUser.bill || curUser.billTo));
          const st = curUser.bill?.state || curUser.billTo?.state || '';
          setBillingState(typeof st === 'object' ? (st._id || st.name || '') : st || '');
        }
        const shipList = curUser.shipTo || curUser.shippingAddresses || curUser.shipToList || [];
        setShipToList(Array.isArray(shipList) ? shipList : []);
        if (Array.isArray(shipList) && shipList.length > 0) {
          setShippingAddress(formatShipToAddress(shipList[0]));
          const sst = shipList[0].state || '';
          setShippingState(typeof sst === 'object' ? (sst._id || sst.name || '') : sst || '');
          setSelectedShipToId(shipList[0]._id || shipList[0].id || '');
        }
      }

      const navState = location && location.state ? location.state : {};

      const normalizeNavCart = (c) => {
        if (!c) return null;
        if (Array.isArray(c)) return c;
        if (typeof c === 'object') {
          return Object.entries(c).map(([itemId, qty]) => ({ itemId, qty }));
        }
        return null;
      };

      const navCart = normalizeNavCart(navState.cartArr) || normalizeNavCart(navState.cartArrLocal) || null;

      const storedCart = localStorage.getItem('cart');
      const parsedStored = storedCart ? JSON.parse(storedCart) : [];

      const cartArr = Array.isArray(navCart) && navCart.length ? navCart : Array.isArray(parsedStored) ? parsedStored : [];

      if (!cartArr || cartArr.length === 0) return;

      // Ensure itemOptions loaded
      if (!itemOptions || itemOptions.length === 0) return;

        const mapped = cartArr.map((ci) => {
        const it = itemOptions.find((o) => String(o._id || o.id) === String(ci.itemId));
        const qty = Number(ci.qty || 0) || 0;
        
        let rate = it ? Number(it.rate || it.price || 0) : 0;
        if (it) {
          if (typeof it.offerPrice !== 'undefined' && it.offerPrice !== null) rate = Number(it.offerPrice);
          else {
            const srList = Array.isArray(it.stateRate) ? it.stateRate : [];
            const stateKey = shippingState || billingState || "";
            const sr = srList.find((s) => {
              const stId = s && s.state ? (s.state._id || s.state) : null;
              return stId && String(stId) === String(stateKey);
            });
            if (sr && typeof sr.price !== 'undefined') rate = Number(sr.price);
            else if (typeof it.salesPrice !== 'undefined' && it.salesPrice !== null) rate = Number(it.salesPrice);
          }
        }
        const total = qty * rate;

        const inter = it?.interStateGst || it?.interState || it?.interGst || null;
        const intra = it?.intraStateGst || it?.intraState || it?.intraGst || null;
        const taxComps = [];
        const useInter = billingState && shippingState ? String(billingState) !== String(shippingState) : false;
        if (useInter && inter && (typeof inter.percentage !== 'undefined' || Array.isArray(inter))) {
          const p = Number(inter.percentage) || 0;
          taxComps.push({ name: inter.name || 'IGST', type: inter.gstType || 'IGST', percentage: p, amount: (qty * rate * p) / 100 });
        } else if (!useInter && intra && Array.isArray(intra.taxes) && intra.taxes.length) {
          for (const t of intra.taxes) {
            const p = Number(t.percentage) || 0;
            taxComps.push({ name: t.name || t.gstType || 'GST', type: t.gstType || t.name || '', percentage: p, amount: (qty * rate * p) / 100 });
          }
        } else if (!useInter) {
          // same state but no structured intra taxes: if a single tax percentage exists, split into SGST/CGST
          const p = Number(it?.taxRate ?? it?.gstRate ?? it?.gst ?? it?.tax ?? it?.percentage ?? 0) || 0;
          if (p) {
            const half = p / 2;
            taxComps.push({ name: 'SGST', type: 'SGST', percentage: half, amount: (qty * rate * half) / 100 });
            taxComps.push({ name: 'CGST', type: 'CGST', percentage: half, amount: (qty * rate * half) / 100 });
          }
        } else {
          const p = Number(it?.taxRate ?? it?.gstRate ?? it?.gst ?? it?.tax ?? it?.percentage ?? 0) || 0;
          if (p) taxComps.push({ name: it?.gstName || it?.taxName || 'GST', type: it?.gstType || '', percentage: p, amount: (qty * rate * p) / 100 });
        }

        return {
          item: it?._id || it?.id || ci.itemId,
          // Do NOT prefill line description from cart (user requested blank)
          description: '',
          unit: it?.unit?.name || it?.unit || '',
          quantity: qty,
          rate,
          taxRate: taxComps.length ? taxComps.reduce((s, c) => s + (Number(c.percentage) || 0), 0) : (it?.taxRate || it?.gstRate || 0),
          taxName: taxComps.length ? taxComps.map((c) => c.name).join(' ') : (it?.taxName || it?.gstName || ''),
          taxType: taxComps.length ? taxComps.map((c) => c.type).join(' ') : (it?.taxType || ''),
          taxComponents: taxComps,
          taxAmount: taxComps.reduce((s, c) => s + (Number(c.amount) || 0), 0),
          total,
        };
      });

      if (mapped.length) {
        setItems(mapped);
      }
    } catch (err) {
      console.error('Failed to prefill order from cart', err);
    }
  }, [itemOptions, user, id]);


  useEffect(() => {
    if (!items || !items.length) return;
    if (!itemOptions || itemOptions.length === 0) return;
    setItems((prev) =>
      prev.map((line) => {
        try {
          const itMeta = itemOptions.find((o) => String(o._id || o.id) === String(line.item));
          const qty = Number(line.quantity || 0);
          const rate = Number(line.rate || 0);
          if (!itMeta) return line;
          const comps = computeTaxFor(itMeta, qty, rate, billingState, shippingState);
          const taxAmt = comps.reduce((s, c) => s + (Number(c.amount) || 0), 0);
          return { ...line, taxComponents: comps, taxAmount: taxAmt, taxRate: comps.reduce((s,c)=>s+(Number(c.percentage)||0),0), taxName: comps.map(c=>c.name).join(' '), taxType: comps.map(c=>c.type).join(' ') };
        } catch (e) {
          return line;
        }
      })
    );
  }, [billingState, shippingState, itemOptions]);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    const fetchOrder = async () => {
      try {
        const res = await axios.get(`/admin/api/order/${id}`);
        const data = res?.data || res;
        const o = data?.order || data;
        if (!o || !mounted) return;

        // Basic fields
        setCustomerName(o.clientName?._id || o.clientName || "");
        setCustomerDisplayName(
          (o.clientName && ((o.clientName.firstName || "") + (o.clientName.lastName ? ` ${o.clientName.lastName}` : ""))) || ""
        );
        // Ensure client billing/shipping info is populated in the form
        try {
          const clientId = o.clientName?._id || o.clientName || "";
          if (clientId) handleClientSelect(clientId);
        } catch (e) {
          // ignore
        }
        setOrderDate(o.date ? new Date(o.date).toISOString().slice(0, 10) : (o.orderdate ? new Date(o.orderdate).toISOString().slice(0,10) : ""));
        setEwayBill(o.eway || o.ewayBill || "");
        setLrNumber(o.lrNo || o.lrNumber || "");
        setTransportNumber(o.transportNo || o.transportNumber || "");

        // Addresses
        const billingAddrObj = o.clientBillingAddress || (o.clientName && o.clientName.bill) || null;
        setBillingAddress(billingAddrObj ? formatBillAddress(billingAddrObj) : "");
        const billStateVal = billingAddrObj ? (typeof billingAddrObj.state === 'object' ? (billingAddrObj.state._id || billingAddrObj.state.name) : billingAddrObj.state) : "";
        setBillingState(billStateVal || "");

        // ensure shipStateVal is declared in this scope so later item mapping can reference it
        let shipStateVal = "";
        if (o.shipping && typeof o.shipping === 'object' && (o.shipping.address || o.shipping.houseNo || o.shipping.societyName)) {
          setShippingAddress(o.shipping.address || [o.shipping.houseNo, o.shipping.societyName].filter(Boolean).join(', '));
          shipStateVal = o.shipping && (typeof o.shipping.state === 'object' ? (o.shipping.state._id || o.shipping.state.name) : o.shipping.state) || "";
          setShippingState(shipStateVal || "");
        } else {
          setShippingAddress("");
          setShippingState("");
        }

        // Items
        const mappedItems = (o.items || []).map((entry) => {
          const it = entry.item || {};
          const useInterLocal = billStateVal && shipStateVal ? String(billStateVal) !== String(shipStateVal) : false;
          return {
            item: it._id || it.id || entry.item || "",
            description: entry.description || it.description || it.desc || "",
            unit: it.unit?.name || it.unit || entry.unit || "",
            quantity: Number(entry.quantity || entry.qty || 0),
            rate: Number(entry.price != null ? entry.price : entry.rate != null ? entry.rate : it.salesPrice || 0),
            taxRate: entry.gstRate != null ? entry.gstRate : entry.gst || (it.interStateGst?.percentage ?? (it.intraStateGst?.taxes ? (Array.isArray(it.intraStateGst.taxes) ? it.intraStateGst.taxes.reduce((s,t)=>s+(Number(t.percentage)||0),0) : "") : "")) || "",
            taxName: entry.gstName || entry.taxName || (it.interStateGst?.name ?? (it.intraStateGst?.taxes ? (Array.isArray(it.intraStateGst.taxes) ? it.intraStateGst.taxes.map(t=>t.name||t.gstType||"GST").join(" ") : "") : "")) || "",
            taxType: entry.gstType || entry.taxType || (it.interStateGst?.gstType ?? (it.intraStateGst?.taxes ? (Array.isArray(it.intraStateGst.taxes) ? it.intraStateGst.taxes.map(t=>t.gstType||"").join(" ") : "") : "")) || "",
            // compute total and taxComponents
            total: Number(entry.totalPrice != null ? entry.totalPrice : entry.total || (entry.quantity && entry.price ? entry.quantity * entry.price : 0)),
            taxComponents: (() => {
              const qty = Number(entry.quantity || entry.qty || 0);
              const price = Number(entry.price != null ? entry.price : entry.rate != null ? entry.rate : it.salesPrice || 0);
              const comps = [];
              const inter = it.interStateGst || it.interState || null;
              const intra = it.intraStateGst || it.intraState || null;
              if (useInterLocal && inter && (typeof inter.percentage !== 'undefined' || Array.isArray(inter))) {
                const p = Number(inter.percentage) || 0;
                comps.push({ name: inter.name || 'IGST', type: inter.gstType || 'IGST', percentage: p, amount: (qty * price * p) / 100 });
              } else if (!useInterLocal && intra && Array.isArray(intra.taxes)) {
                for (const t of intra.taxes) {
                  const p = Number(t.percentage) || 0;
                  comps.push({ name: t.name || t.gstType || 'GST', type: t.gstType || t.name || '', percentage: p, amount: (qty * price * p) / 100 });
                }
              } else if (entry.gstRate || entry.gst) {
                // determine whether to split into SGST/CGST or keep single component based on billing/shipping states
                const p = Number(entry.gstRate || entry.gst || 0);
                if (!useInterLocal) {
                  const half = p / 2;
                  comps.push({ name: 'SGST', type: 'SGST', percentage: half, amount: (qty * price * half) / 100 });
                  comps.push({ name: 'CGST', type: 'CGST', percentage: half, amount: (qty * price * half) / 100 });
                } else {
                  comps.push({ name: entry.gstName || entry.taxName || 'GST', type: entry.gstType || '', percentage: p, amount: (qty * price * p) / 100 });
                }
              }
              return comps;
            })(),
            taxAmount: (() => {
              const qty = Number(entry.quantity || entry.qty || 0);
              const price = Number(entry.price != null ? entry.price : entry.rate != null ? entry.rate : it.salesPrice || 0);
              const inter = it.interStateGst || it.interState || null;
              const intra = it.intraStateGst || it.intraState || null;
              let tot = 0;
              const useInterLocal = billStateVal && shipStateVal ? String(billStateVal) !== String(shipStateVal) : false;
              if (useInterLocal && inter && (typeof inter.percentage !== 'undefined' || Array.isArray(inter))) {
                const p = Number(inter.percentage) || 0;
                tot += (qty * price * p) / 100;
              } else if (!useInterLocal && intra && Array.isArray(intra.taxes)) {
                for (const t of intra.taxes) {
                  const p = Number(t.percentage) || 0;
                  tot += (qty * price * p) / 100;
                }
              } else if (entry.gstRate || entry.gst) {
                const p = Number(entry.gstRate || entry.gst || 0);
                if (!useInterLocal) {
                  // split
                  const half = p / 2;
                  tot += (qty * price * half) / 100;
                  tot += (qty * price * half) / 100;
                } else {
                  tot += (qty * price * p) / 100;
                }
              }
              return tot;
            })(),
          };
        });
        setItems(mappedItems.length ? mappedItems : items);

        // Totals & other
        setDiscount(o.discount != null ? o.discount : 0);
        setAdjustments(o.adjustments != null ? o.adjustments : 0);
        setExpectedDeliveryDate(o.expectedDate ? new Date(o.expectedDate).toISOString().slice(0,10) : "");
        setTerms(o.termsandCondition || o.terms || "");
        setNotes(o.note || o.notes || "");
        setStatus(o.status || "");

        // Clients/shipTo list if available
        if (o.clientName) {
          const st = o.clientName.shipTo || o.clientName.shipToList || o.clientName.shippingAddresses || [];
          setShipToList(Array.isArray(st) ? st : []);
        }
      } catch (err) {
        console.error('Failed to fetch order for edit', err);
      }
    };
    fetchOrder();
    return () => { mounted = false; };
  }, [id]);

  const fetchCountries = async () => {
    try {
      const res = await axios.get("/admin/api/country");
      const payload = res?.data;
      const list = Array.isArray(payload?.countries) ? payload.countries : Array.isArray(payload) ? payload : [];
      setCountries(list);
      // preselect default country id if available
      const def = list.find((c) => c.default || c.name === "India");
      if (def) setShippingForm((s) => ({ ...s, country: s.country || def._id }));
    } catch (err) {
      console.error("Failed to fetch countries", err);
    }
  };

  const fetchStates = async (countryParam) => {
    if (!countryParam) {
      setStatesList([]);
      return;
    }
    try {
      const res = await axios.get(`/admin/api/state?country=${encodeURIComponent(countryParam)}`);
      const payload = res?.data;
      const list = Array.isArray(payload?.states) ? payload.states : Array.isArray(payload) ? payload : [];
      setStatesList(list);
    } catch (err) {
      console.error("Failed to fetch states", err);
    }
  };

  const fetchCities = async (stateParam) => {
    if (!stateParam) {
      setCitiesList([]);
      return;
    }
    try {
      const res = await axios.get(`/admin/api/city?state=${encodeURIComponent(stateParam)}`);
      const payload = res?.data;
      const list = Array.isArray(payload?.cities) ? payload.cities : Array.isArray(payload) ? payload : [];
      setCitiesList(list);
    } catch (err) {
      console.error("Failed to fetch cities", err);
    }
  };

  const formatBillAddress = (bill) => {
    if (!bill) return "";
    const cityName = bill.city?.name || bill.city || "";
    const stateName = bill.state?.name || bill.state || "";
    const countryName = bill.country?.name || bill.country || "";
    return [
      bill.houseNo,
      bill.societyName,
      bill.area,
      bill.landmark,
      bill.address,
      cityName,
      stateName,
      countryName,
      bill.pinCode,
    ]
      .filter(Boolean)
      .join(", ");
  };

  const formatShipToAddress = (s) => {
    if (!s) return "";
    const city = (typeof s.city === "object" ? s.city.name : s.city) || "";
    const state = (typeof s.state === "object" ? s.state.name : s.state) || "";
    const country = (typeof s.country === "object" ? s.country.name : s.country) || "";
    return [s.houseNo, s.societyName, s.area, s.landmark, city, state, country, s.pinCode]
      .filter(Boolean)
      .join(", ");
  };

  const handleClientSelect = async (clientId) => {
    setCustomerName(clientId);

    let client = clients.find((c) => c._id === clientId || c.id === clientId);

    if (!client && clientId) {
      try {
        const res = await axios.get(`/api/user/${clientId}`);
        client = res?.data?.data?.user || res?.data?.user || res?.data?.data || res?.data;
      } catch (err) {
        console.error("Failed to fetch client details", err);
      }
    }

    if (client) {
      const display = (
        client.firstName ||
        client.name ||
        client.business ||
        client.email ||
        client.title ||
        ""
      )
        .toString()
        .trim();
      setCustomerDisplayName(display);
      const bill = client.bill || client.billing || client.billingAddress || client.address || null;
      setBillingAddress(formatBillAddress(bill));
      // store billing state for GST decision (id or name)
      const billStateVal = bill ? (typeof bill.state === 'object' ? (bill.state._id || bill.state.name) : bill.state) : "";
      setBillingState(billStateVal || "");

      const shipTo =
        client.shipTo ||
        client.shipToList ||
        client.shippingAddresses ||
        client.addresses?.shipping ||
        [];
      setShipToList(shipTo || []);

      if (Array.isArray(shipTo) && shipTo.length > 0) {
        const first = shipTo[0];
        setSelectedShipToId(first._id || first.id || "");
        setShippingAddress(formatShipToAddress(first));
        const firstState = first ? (typeof first.state === 'object' ? (first.state._id || first.state.name) : first.state) : "";
        setShippingState(firstState || "");
      } else {
        setSelectedShipToId("");
        setShippingAddress("");
      }
    } else {
      setShipToList([]);
      setSelectedShipToId("");
      setBillingAddress("");
      setShippingAddress("");
      setCustomerDisplayName("");
    }
  };

  const addItemRow = () => {
    setItems((prev) => [
      ...prev,
      {
        item: "",
        description: "",
        unit: "",
        quantity: 1,
        rate: 0,
        taxRate: "",
        taxName: "",
        taxType: "",
        taxAmount: 0,
        taxComponents: [],
        total: 0,
      },
    ]);
  };

  const removeItemRow = (index) => {
    setItems((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) {
        return [
          {
            item: "",
            description: "",
            unit: "",
            quantity: 1,
            rate: 0,
            taxRate: "",
            taxName: "",
            taxType: "",
            taxAmount: 0,
            taxComponents: [],
            total: 0,
          },
        ];
      }
      return next;
    });
  };

  // ── Modal Handlers ──
  const handleEditShipping = () => {
    if (selectedShipToId) {
      const selected = shipToList.find((s) => s._id === selectedShipToId);
      if (selected) {
        setShippingForm({
          houseNo: selected.houseNo || "",
          societyName: selected.societyName || "",
          area: selected.area || "",
          landmark: selected.landmark || "",
          city: (typeof selected.city === "object" ? selected.city._id || selected.city.name : selected.city) || "",
          state: (typeof selected.state === "object" ? selected.state._id || selected.state.name : selected.state) || "",
          country: (typeof selected.country === "object" ? selected.country._id || selected.country.name : selected.country) || "",
          pinCode: selected.pinCode || "",
        });
        // fetch dependent lists (will also be handled by effects below)
        const countryParam = (typeof selected.country === "object" ? selected.country._id || selected.country.name : selected.country) || selected.country;
        fetchStates(countryParam);
        const stateParam = (typeof selected.state === "object" ? selected.state._id || selected.state.name : selected.state) || selected.state;
        fetchCities(stateParam);
        // set shipping state for GST decision
        const stVal = selected ? (typeof selected.state === 'object' ? (selected.state._id || selected.state.name) : selected.state) : "";
        setShippingState(stVal || "");
        setShowShippingModal(true);
        return;
      }
    }

    // If no selection or custom → start blank or try to parse current shippingAddress (optional)
    setShippingForm({
      houseNo: "",
      societyName: "",
      area: "",
      landmark: "",
      city: "",
      state: "",
      country: "India",
      pinCode: "",
    });
    // ensure states/cities cleared
    setStatesList([]);
    setCitiesList([]);
    setShowShippingModal(true);
  };

  const handleAddShipping = () => {
    // Prepare a blank form for adding a new address
    const defCountry = (countries || []).find((c) => c.default || c.name === "India")?._id || "India";
    setSelectedShipToId("");
    setShippingForm({
      houseNo: "",
      societyName: "",
      area: "",
      landmark: "",
      city: "",
      state: "",
      country: defCountry,
      pinCode: "",
    });
    setStatesList([]);
    setCitiesList([]);
    if (defCountry) fetchStates(defCountry);
    setShowShippingModal(true);
  };

  useEffect(() => {
    if (shippingForm.country) {
      fetchStates(shippingForm.country);
      setCitiesList([]);
      setShippingForm((s) => ({ ...s, state: "", city: "" }));
    } else {
      setStatesList([]);
    }
  
  }, [shippingForm.country]);

  useEffect(() => {
    if (shippingForm.state) {
      fetchCities(shippingForm.state);
      setShippingForm((s) => ({ ...s, city: "" }));
    } else {
      setCitiesList([]);
    }
  }, [shippingForm.state]);

  const handleSaveShipping = () => {
    const countryName = (countries.find((c) => c._id === shippingForm.country) || {}).name || shippingForm.country;
    const stateName = (statesList.find((s) => s._id === shippingForm.state) || {}).name || shippingForm.state;
    const cityName = (citiesList.find((c) => c._id === shippingForm.city) || {}).name || shippingForm.city;

    const parts = [
      shippingForm.houseNo ? `House/Office: ${shippingForm.houseNo}` : "",
      shippingForm.societyName,
      shippingForm.area,
      shippingForm.landmark ? `Near ${shippingForm.landmark}` : "",
      cityName,
      stateName,
      shippingForm.pinCode ? `PIN: ${shippingForm.pinCode}` : "",
      countryName,
    ].filter(Boolean);

    const fullAddress = parts.join(", ");
    // If editing an existing saved address, update it in the shipToList
    if (selectedShipToId) {
      const updated = (shipToList || []).map((s) => {
        const sid = s._id || s.id || s.key || "";
        if (sid === selectedShipToId) {
          return {
            ...s,
            houseNo: shippingForm.houseNo || s.houseNo || "",
            societyName: shippingForm.societyName || s.societyName || "",
            area: shippingForm.area || s.area || "",
            landmark: shippingForm.landmark || s.landmark || "",
            city: shippingForm.city || s.city || "",
            state: shippingForm.state || s.state || "",
            country: shippingForm.country || s.country || "",
            pinCode: shippingForm.pinCode || s.pinCode || "",
            address: fullAddress || s.address || "",
          };
        }
        return s;
      });
      setShipToList(updated);
      setShippingAddress(fullAddress || "");
      setShippingState(shippingForm.state || "");
    } else {
      // Add a new temporary saved address locally and select it
      const newId = `local_${Date.now()}`;
      const newEntry = {
        _id: newId,
        houseNo: shippingForm.houseNo || "",
        societyName: shippingForm.societyName || "",
        area: shippingForm.area || "",
        landmark: shippingForm.landmark || "",
        city: shippingForm.city || "",
        state: shippingForm.state || "",
        country: shippingForm.country || "",
        pinCode: shippingForm.pinCode || "",
        address: fullAddress || "",
      };
      const updated = [...(shipToList || []), newEntry];
      setShipToList(updated);
      setSelectedShipToId(newId);
      setShippingAddress(fullAddress || "");
      setShippingState(shippingForm.state || "");
    }
    setShowShippingModal(false);
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    try {
      if (!isOrderEditable) {
        toastError("This order cannot be edited in its current status.");
        return;
      }
      
      if (id) {
        try {
          const statusRes = await axios.get(`/admin/api/order/${id}`);
          const serverOrder = statusRes?.data?.order || statusRes?.data || statusRes;
          const serverStatus = serverOrder?.status || serverOrder?.order?.status || "";
          if (serverStatus && ["Confirmed", "Partially Dispatched", "Fully Dispatched"].includes(serverStatus)) {
            toastError("This order cannot be edited because its status is " + serverStatus + ".");
            return;
          }
        } catch (err) {
          
          console.warn('Failed to verify server order status before update', err);
        }
      }
      
      if (!billingAddress || !billingAddress.toString().trim()) {
        toastError("Client billing address is missing. Please select a client with a billing address.");
        return;
      }
      const usedShippingAddress = shippingAddress && shippingAddress.toString().trim() ? shippingAddress : billingAddress;

      const apiPayload = {
        clientName: customerName,
        date: orderDate,
        expectedDate: expectedDeliveryDate,
        eway: ewayBill,
        lrNo: lrNumber,
        transportNo: transportNumber,
        shipping: (() => {
          if (selectedShipToId) {
            const sel = shipToList.find((s) => s._id === selectedShipToId) || {};
            return {
              houseNo: sel.houseNo || "",
              societyName: sel.societyName || "",
              area: sel.area || "",
              landmark: sel.landmark || "",
              address: formatShipToAddress(sel) || usedShippingAddress || billingAddress || "",
              state: sel.state || "",
              city: sel.city || "",
              country: sel.country || "",
              pincode: sel.pinCode || "",
            };
          }
          return {
            houseNo: "",
            societyName: "",
            area: "",
            landmark: "",
            address: usedShippingAddress || billingAddress || "",
            state: "",
            city: "",
            country: "",
            pincode: "",
          };
        })(),
        items: items.map((it) => ({
          item: it.item,
          quantity: Number(it.quantity) || 0,
          price: Number(it.rate) || 0,
          gstRate: it.taxRate || "",
          gstName: it.taxName || "",
          gstType: it.taxType || "",
        })),
        note: notes,
        termsandCondition: terms,
        status: status,
      };

      if (id) {
        await axios.put(`/admin/api/order/${id}`, apiPayload);
        toastSuccess("Order updated successfully");
      } else {
        await axios.post("/admin/api/order", apiPayload);
        toastSuccess("Order created successfully");
      }

      // Redirect clients to their user orders page, otherwise admin orders
      const dest = user && user.userType === "Client" ? "/user/order" : "/admin/order";
      navigate(dest);
    } catch (err) {
      toastError("Failed to create order");
    }
  };

  // Build tax breakdown per tax name for display in totals
  const taxBreakdownMap = items.reduce((map, it) => {
    (it.taxComponents || []).forEach((c) => {
      const key = (c.type || c.name || "GST").toString();
      if (!map[key]) map[key] = { amount: 0, percents: new Set() };
      map[key].amount += Number(c.amount) || 0;
      if (typeof c.percentage !== 'undefined' && c.percentage !== null) map[key].percents.add(Number(c.percentage) || 0);
    });
    return map;
  }, {});
  const taxSummary = Object.entries(taxBreakdownMap).map(([name, v]) => ({
    name,
    amount: v.amount || 0,
    percent: v.percents.size === 1 ? Array.from(v.percents)[0] : null,
  }));

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h2 fw-bold">Create Order</h1>
        <a
          href={user && user.userType === "Client" ? "/user" : "/admin/order"}
          onClick={(e) => {
            e.preventDefault();
            navigate(user && user.userType === "Client" ? "/user" : "/admin/order");
          }}
          className="text-decoration-none text-secondary"
        >
          ← Back
        </a>
      </div>

      <div className="card-bg">
        <form className="item-form m-4" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          {/* CUSTOMER */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <label className="form-label fw-medium required">Customer Name</label>
              <select
                className="form-select"
                  value={customerName}
                  onChange={(e) => handleClientSelect(e.target.value)}
                  disabled={!isOrderEditable}
              >
                <option value="">Select client</option>
                {clients.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.firstName} {c.lastName} {c.business ? `(${c.business})` : `(${c.email})`}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-medium required">Date</label>
              <input
                type="date"
                className="form-control"
                value={orderDate}
                onChange={(e) => setOrderDate(e.target.value)}
                disabled={!isOrderEditable}
              />
            </div>
            {!isClientCreate && (
              <>
                <div className="col-md-3">
                  <label className="form-label fw-medium">E-Way Bill No</label>
                  <input className="form-control" value={ewayBill} onChange={(e) => setEwayBill(e.target.value)} disabled={!isOrderEditable} />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-medium">LR Number</label>
                  <input className="form-control" value={lrNumber} onChange={(e) => setLrNumber(e.target.value)} disabled={!isOrderEditable} />
                </div>
              </>
            )}
          </div>

          {!isClientCreate && (
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <label className="form-label fw-medium">Transport Number</label>
                <input
                  className="form-control"
                  value={transportNumber}
                  onChange={(e) => setTransportNumber(e.target.value)}
                  disabled={!isOrderEditable}
                />
              </div>
            </div>
          )}

          <hr className="mb-4 border-secondary" />

          {/* ADDRESS */}
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <label className="form-label fw-medium">Billing Address</label>
              <textarea
                className="form-control"
                rows="3"
                value={billingAddress}
                readOnly
              />
            </div>

            <div className="col-md-6">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label fw-medium">Shipping Address</label>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleEditShipping}
                  disabled={!isOrderEditable}
                >
                  <i className="fas fa-edit me-1"></i> Edit
                </button>
              </div>

              {shipToList.length > 0 && (
                <select
                  className="form-select mb-2"
                  value={selectedShipToId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedShipToId(id);
                    const s = shipToList.find((st) => st._id === id);
                    if (s) {
                      setShippingAddress(formatShipToAddress(s));
                      const stVal = s ? (typeof s.state === 'object' ? (s.state._id || s.state.name) : s.state) : "";
                      setShippingState(stVal || "");
                    }
                  }}
                >
                  <option value="">Select saved address</option>
                  {shipToList.map((st) => (
                    <option key={st._id} value={st._id}>
                      {formatShipToAddress(st).substring(0, 60)}
                      {formatShipToAddress(st).length > 60 ? "..." : ""}
                    </option>
                  ))}
                </select>
              )}

              <div className="mb-2">
                <button type="button" className="btn btn-sm btn-primary" onClick={handleAddShipping}>
                  + Add Another Address
                </button>
              </div>

                <textarea
                  className="form-control"
                  rows="3"
                  value={shippingAddress || "No shipping address selected"}
                  readOnly
                  placeholder="Click 'Edit' to set shipping address"
                />
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-4">
            <h5 className="mb-3 fw-semibold">Items</h5>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Description</th>
                    <th>Unit</th>
                    <th>Qty</th>
                    <th>Rate</th>
                    <th>Tax (%)</th>
                    <th>Tax Amt</th>
                    <th>Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <select
                          className="form-select"
                          value={row.item}
                          onChange={(e) => handleItemChange(i, "item", e.target.value)}
                          disabled={!isOrderEditable}
                        >
                          <option value="">Select item</option>
                          {itemOptions.map((it) => (
                            <option key={it._id || it.id} value={it._id || it.id}>
                              {it.name || it.itemName || it.title || it._id}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          className="form-control"
                          value={row.description}
                          onChange={(e) => handleItemChange(i, "description", e.target.value)}
                          readOnly={isCreateMode || !isOrderEditable}
                          disabled={!isOrderEditable}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control"
                          value={row.unit}
                          onChange={(e) => handleItemChange(i, "unit", e.target.value)}
                          readOnly={isCreateMode || !isOrderEditable}
                          disabled={!isOrderEditable}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control text-end"
                          value={row.quantity}
                          onChange={(e) => handleItemChange(i, "quantity", e.target.value)}
                          disabled={!isOrderEditable}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control text-end"
                          value={row.rate}
                          onChange={(e) => handleItemChange(i, "rate", e.target.value)}
                          readOnly={isCreateMode || !isOrderEditable}
                          disabled={isCreateMode || !isOrderEditable}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control text-end"
                          value={
                            Array.isArray(row.taxComponents) && row.taxComponents.length
                              ? row.taxComponents.map(c => `${(c.type||c.name||'GST')} ${Number(c.percentage)||0}%`).join(' ')
                              : (row.taxType || row.taxName ? `${row.taxType || row.taxName} ${row.taxRate}%` : (row.taxRate !== "" ? `${row.taxRate}%` : ""))
                          }
                          readOnly
                          disabled
                          placeholder="0"
                        />
                      </td>
                        <td className="text-end">₹ {Number(derived.perLine[i]?.taxAmount || 0).toFixed(2)}</td>
                        <td className="text-end fw-medium">{Number(derived.perLine[i]?.netBase || 0).toFixed(2)}</td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => removeItemRow(i)}
                          disabled={!isOrderEditable}
                        >
                          -
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button type="button" className="btn btn-sm btn-primary mt-2" onClick={addItemRow}>
              + Add Item
            </button>
          </div>

          {/* Totals */}
          <div className="row justify-content-end mb-4">
            <div className="col-md-5 col-lg-4">
              <div className="totals-box">
                <div className="d-flex justify-content-between">
                  <span>Subtotal</span>
                  <span>₹ {subTotal.toFixed(2)}</span>
                </div>
                {!isClientUser && (
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Discount</span>
                    <input
                      type="number"
                      className="form-control w-50 text-end"
                      value={discount}
                      onChange={(e) => setDiscount(Number(e.target.value) || 0)}
                      disabled={!isOrderEditable}
                    />
                  </div>
                )}
                {/* Tax breakdown lines */}
                {/** Build tax summary from derived.perLine */}
                {derived && derived.perLine && derived.perLine.length > 0 && (
                  <div style={{ marginTop: 8, marginBottom: 8 }}>
                    {(() => {
                      const map = {};
                      derived.perLine.forEach((p, idx) => {
                        (p.components || []).forEach((c) => {
                          const key = (c.type || c.name || 'GST').toString();
                          if (!map[key]) map[key] = { amount: 0, percents: new Set() };
                          map[key].amount += Number(c.amount) || 0;
                          if (typeof c.percentage !== 'undefined' && c.percentage !== null) map[key].percents.add(Number(c.percentage) || 0);
                        });
                      });
                      return Object.entries(map).map(([name, v]) => (
                        <div key={name} className="d-flex justify-content-between" style={{ fontSize: '0.95rem' }}>
                          <span>{name}{v.percents.size === 1 ? ` ${Array.from(v.percents)[0]}%` : ""}</span>
                          <span>₹ {Number(v.amount || 0).toFixed(2)}</span>
                        </div>
                      ));
                    })()}
                  </div>
                )}
                {!isClientUser && (
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Adjustment</span>
                    <input
                      type="number"
                      className="form-control w-50 text-end"
                      value={adjustments}
                      onChange={(e) => setAdjustments(Number(e.target.value) || 0)}
                      disabled={!isOrderEditable}
                    />
                  </div>
                )}
                <hr />
                <div className="d-flex justify-content-between grand-total">
                  <span>Total</span>
                  <span>₹ {grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer fields */}
          <div className="row g-3 mb-4">
            <div className="col-md-2">
              <label className="form-label fw-medium required">Expected Delivery Date</label>
              <input
                type="date"
                className="form-control"
                value={expectedDeliveryDate}
                  onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                  disabled={!isOrderEditable}
              />
            </div>
            {!isClientUser && (
              <>
                <div className="col-md-2">
                  <label className="form-label fw-medium">Terms & Conditions</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={terms}
                      onChange={(e) => setTerms(e.target.value)}
                      disabled={!isOrderEditable}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-medium">Notes</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      disabled={!isOrderEditable}
                  />
                </div>
              </>
            )}
          </div>

          {!isClientCreate && (
            <div className="row mb-4">
              <div className="col-md-2">
                <label className="form-label fw-medium">Status</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={!isOrderEditable}
                >
                  <option value="">Select</option>
                  <option value="New">New</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Partially Dispatched">Partially Dispatched</option>
                  <option value="Fully Dispatched">Fully Dispatched</option>
                </select>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end gap-3">
            <button type="button" className="btn btn-secondary px-4" onClick={() => navigate(-1)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary px-5">
              Save Order
            </button>
          </div>
        </form>
      </div>

      {/* ── Shipping Address Edit Modal (CommonModal) ── */}
      <CommonModal
        visible={showShippingModal}
        title="Edit Shipping Address"
        values={shippingForm}
        onChange={(vals) => setShippingForm(vals)}
        onCancel={() => setShowShippingModal(false)}
        onSubmit={handleSaveShipping}
        submitLabel="Update Address"
        cancelLabel="Cancel"
        fields={[
          { name: 'houseNo', label: 'Office / House Number', type: 'text', placeholder: 'e.g. 604' },
          { name: 'societyName', label: 'Building / Society Name', type: 'text', placeholder: 'e.g. Sahitya Business Park' },
          { name: 'landmark', label: 'Landmark / Nearby', type: 'text', placeholder: 'e.g. Near Kathwada' },
          { name: 'area', label: 'Area / Street Name', type: 'text', placeholder: 'e.g. Odhav' },
          { name: 'country', label: 'Country', type: 'select', options: (countries || []).map(c => ({ value: c._id || c.name, label: c.name || c._id })) },
          { name: 'state', label: 'State', type: 'select', options: (statesList || []).map(s => ({ value: s._id || s.name, label: s.name || s._id })) },
          { name: 'city', label: 'City', type: 'select', options: (citiesList || []).map(c => ({ value: c._id || c.name, label: c.name || c._id })) },
          { name: 'pinCode', label: 'Pin Code', type: 'text', placeholder: 'e.g. 382260' },
        ]}
      />
    </div>
  );
}